import React, { useEffect, useMemo, useState } from "react";
import ClassStudentPicker from "../EOYReportAssistant/ClassStudentPicker";
import ScreeningPointReportCard from "./ScreeningPointReportCard";
import useEnhancedReportWorkflow from "./useEnhancedReportWorkflow";
import { getStudentDisplayName } from "./enhancedReportsUtils";
import {
  generateBatchScreeningPointReports,
  getScreeningPointClassReports,
} from "../../services/APIClient";

const SCREENING_POINTS = [
  { value: "baseline", label: "Baseline" },
  { value: "midpoint", label: "Midpoint" },
  { value: "end_of_year", label: "End of Year" },
];

/**
 * Screening Point tab. Same shared-selection + per-tab workflow pattern as
 * NewClassInsightsTab, with a required screening-point selector in place of
 * the year pair. `academicYear` is owned by EnhancedAIReportsPanel (not
 * this component) because it's also fed into the shared class-selection
 * hook so the class list matches the selected year.
 */
function ScreeningPointTab({
  selection,
  yearOptions,
  academicYear,
  onAcademicYearChange,
  title,
  subheading,
}) {
  const [screeningPoint, setScreeningPoint] = useState("baseline");

  const extraFields = useMemo(
    () => ({ screeningPoint, academicYear }),
    [screeningPoint, academicYear]
  );
  const extraFieldsKey = `${screeningPoint}|${academicYear}`;

  const buildStudentPayload = (studentId) => ({
    studentId,
    screeningPoint,
    academicYear,
  });

  const workflow = useEnhancedReportWorkflow({
    generateBatch: generateBatchScreeningPointReports,
    getClassReports: getScreeningPointClassReports,
    buildStudentPayload,
    extraFields,
  });

  useEffect(() => {
    workflow.resetReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection.selectedClassId, extraFieldsKey]);

  useEffect(() => {
    if (!selection.selectedClassId || selection.studentsLoading) return;
    const ids = selection.students.map((student) => student.id).filter(Boolean);
    if (ids.length > 0) workflow.loadSavedReports(ids);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selection.selectedClassId,
    selection.studentsLoading,
    selection.students,
    extraFieldsKey,
  ]);

  const handleGenerate = () => {
    if (!selection.disclaimerAccepted) return;

    const selectedStudents = selection.selectedStudentIds
      .filter((id) => !workflow.doneStudentIds.includes(id))
      .map((id) => {
        const student = selection.students.find((s) => s.id === id);
        return { student_id: id, display_name: getStudentDisplayName(student, id) };
      });

    if (selectedStudents.length === 0) return;
    workflow.generate(selectedStudents);
  };

  return (
    <div className="eoy-panel-root">
      <div className="eoy-header">
        <h1 className="eoy-title">{title}</h1>
        <p className="eoy-subtitle">{subheading}</p>
      </div>

      <div className="eoy-picker">
        <div className="eoy-field">
          <label className="eoy-field-label" htmlFor="spr-screening-point">
            Screening point
          </label>
          <select
            id="spr-screening-point"
            className="eoy-select"
            value={screeningPoint}
            onChange={(e) => setScreeningPoint(e.target.value)}
            disabled={workflow.generating}
          >
            {SCREENING_POINTS.map((point) => (
              <option key={point.value} value={point.value}>
                {point.label}
              </option>
            ))}
          </select>
        </div>
        <div className="eoy-field">
          <label className="eoy-field-label" htmlFor="spr-academic-year">
            Academic year
          </label>
          <select
            id="spr-academic-year"
            className="eoy-select"
            value={academicYear}
            onChange={(e) => onAcademicYearChange(e.target.value)}
            disabled={workflow.generating}
          >
            {yearOptions.map((year) => (
              <option key={year.storage} value={year.storage}>
                {year.display}
              </option>
            ))}
          </select>
        </div>
      </div>

      <ClassStudentPicker
        classes={selection.filteredClasses}
        classesLoading={selection.classesLoading}
        classesError={selection.classesError}
        selectedGradeLevel={selection.selectedGradeLevel}
        onGradeLevelChange={selection.handleGradeLevelChange}
        selectedClassId={selection.selectedClassId}
        onClassChange={selection.handleClassChange}
        students={selection.students}
        studentsLoading={selection.studentsLoading}
        studentsError={selection.studentsError}
        selectedStudentIds={selection.selectedStudentIds}
        unavailableStudentIds={workflow.doneStudentIds}
        onStudentToggle={(id) =>
          selection.handleStudentToggle(id, workflow.doneStudentIds)
        }
        onSelectAll={() => selection.handleSelectAll(workflow.doneStudentIds)}
        disclaimerAccepted={selection.disclaimerAccepted}
        onDisclaimerAcceptedChange={selection.setDisclaimerAccepted}
        onGenerate={handleGenerate}
        generating={workflow.generating || workflow.savedReportsLoading}
      />

      {workflow.savedReportsLoading && selection.selectedClassId && (
        <div className="eoy-muted eoy-saved-reports-status">
          Checking for saved reports...
        </div>
      )}

      {workflow.generating && workflow.progress.total > 0 && (
        <div className="eoy-progress-meta">
          Generating report{" "}
          {Math.min(workflow.progress.done + 1, workflow.progress.total)} of{" "}
          {workflow.progress.total}...
        </div>
      )}

      {!workflow.generating && workflow.reports.length > 0 && (
        <div className="eoy-done-summary">
          <strong>
            {workflow.reportsSource === "saved"
              ? `${workflow.reports.length} saved report${
                  workflow.reports.length === 1 ? "" : "s"
                } loaded`
              : `${workflow.reports.filter((r) => r.success).length} of ${
                  workflow.reports.length
                } reports generated`}
          </strong>
          {selection.selectedClassName && <span> for {selection.selectedClassName}</span>}
        </div>
      )}

      {workflow.genError && <div className="eoy-error">{workflow.genError}</div>}

      {workflow.reports.map((report) => (
        <ScreeningPointReportCard
          key={report.student_id}
          report={report}
          className={selection.selectedClassName}
          academicYear={academicYear}
          onRegenerateReport={workflow.regenerate}
          regenerating={workflow.generating}
        />
      ))}
    </div>
  );
}

export default ScreeningPointTab;
